import { SITE } from "@/config/site";

/**
 * Automatic SEO metadata generation.
 *
 * Every page only has to describe itself in plain language (title, a short
 * description, an image) — this module fills in the rest the way a technical
 * SEO audit expects: a length-safe title, a keyword-rich description within the
 * 120–160 char sweet spot, derived keywords, a canonical URL, Open Graph and
 * Twitter fields, and a sensible default share image.
 */

export const DEFAULT_SEO_DESCRIPTION =
  "Discover trusted local businesses, services and tourist spots across Mysore, Mandya, Chamarajanagar and Kodagu with the Mysore Yellow Pages directory.";

// Brand social-share card built from the site logo — a 1200×630 navy card with
// the light-on-dark lockup. Served from our OWN domain (mirrored by
// scripts/sync-vendor-assets.mjs into /uploads/brand/og-image.jpg), so no
// third-party CDN sits in the crawler's critical path.
export const DEFAULT_OG_IMAGE = `${SITE.url}/uploads/brand/og-image.jpg`;

/** Recommended Open Graph share-image dimensions (the 1.91:1 card ratio). */
export const OG_RECOMMENDED_WIDTH = 1200;
export const OG_RECOMMENDED_HEIGHT = 630;

export type OgImageStatus = "empty" | "loading" | "ok" | "warning" | "error";

export interface OgImageCheck {
  status: OgImageStatus;
  width: number;
  height: number;
  message: string;
}

/**
 * Loads a share image in the browser and reports whether its dimensions suit a
 * link preview. Used by the admin "social share preview" checker to warn when
 * an uploaded image is not the recommended 1200×630.
 */
export function checkOgImage(url: string): Promise<OgImageCheck> {
  const clean = (url || "").trim();
  if (!clean) {
    return Promise.resolve({
      status: "empty",
      width: 0,
      height: 0,
      message: "No share image set — the built-in brand card will be used.",
    });
  }

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (!w || !h) {
        resolve({ status: "error", width: 0, height: 0, message: "Could not read the image size." });
        return;
      }
      const targetRatio = OG_RECOMMENDED_WIDTH / OG_RECOMMENDED_HEIGHT;
      const isExact = w === OG_RECOMMENDED_WIDTH && h === OG_RECOMMENDED_HEIGHT;
      const isClose = Math.abs(w / h - targetRatio) < 0.02;
      if (isExact) {
        resolve({ status: "ok", width: w, height: h, message: `${w}×${h} — perfect for link previews.` });
      } else if (isClose) {
        resolve({ status: "ok", width: w, height: h, message: `${w}×${h} — good 1.91:1 ratio for link previews.` });
      } else {
        resolve({
          status: "warning",
          width: w,
          height: h,
          message: `${w}×${h} — previews look best at ${OG_RECOMMENDED_WIDTH}×${OG_RECOMMENDED_HEIGHT} (1.91:1).`,
        });
      }
    };
    img.onerror = () =>
      resolve({ status: "error", width: 0, height: 0, message: "The image could not be loaded — check the URL." });
    img.src = clean;
  });
}

const STOP_WORDS = new Set([
  "the", "and", "for", "with", "your", "you", "our", "from", "that", "this",
  "are", "was", "will", "can", "all", "any", "near", "in", "of", "to", "on",
  "at", "a", "an", "is", "it", "by", "as", "or", "we", "us", "get", "find",
  "page", "home", "index", "detail", "listing", "list", "best", "top",
]);

export interface SeoInput {
  title?: string;
  description?: string;
  keywords?: string;
  path?: string;
  image?: string;
}

export interface GeneratedSeo {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  image: string;
  ogType: string;
  twitterCard: string;
}

/** Clamps text to `max` characters without chopping a word in half. */
export function clampText(text: string, max: number): string {
  const clean = text.replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const slice = clean.slice(0, max - 1);
  const lastSpace = slice.lastIndexOf(" ");
  return `${(lastSpace > max * 0.6 ? slice.slice(0, lastSpace) : slice).trim()}…`;
}

/** Builds a tidy, de-duplicated keyword list from a topic, the URL and the brand. */
export function deriveKeywords(topic?: string, path = "/"): string {
  const tokens: string[] = [];
  const seen = new Set<string>();
  const add = (word: string) => {
    const cleaned = word.toLowerCase().replace(/[^a-z0-9]/g, "");
    if (cleaned.length < 3 || STOP_WORDS.has(cleaned) || seen.has(cleaned)) return;
    seen.add(cleaned);
    tokens.push(cleaned);
  };

  (topic ?? "").split(/[\s,/|]+/).forEach(add);
  path.replace(/[/-]/g, " ").split(/\s+/).forEach(add);

  ["mysore directory", "local businesses mysore", "business listings karnataka", "mysore yellow pages"].forEach((phrase) => {
    if (tokens.length < 8) tokens.push(phrase);
  });

  const unique: string[] = [];
  const phraseSeen = new Set<string>();
  for (const t of tokens) {
    if (phraseSeen.has(t)) continue;
    phraseSeen.add(t);
    unique.push(t);
    if (unique.length >= 6) break;
  }
  return unique.join(", ");
}

/** Produces the complete, ready-to-emit SEO set for a page. */
export function buildSeo(input: SeoInput = {}): GeneratedSeo {
  const path = input.path && input.path !== "" ? input.path : "/";
  const title = input.title ? `${input.title} | ${SITE.name}` : `${SITE.name} — Local Business Directory`;
  const description = clampText(input.description?.trim() || DEFAULT_SEO_DESCRIPTION, 160);
  const keywords = clampText(input.keywords?.trim() || deriveKeywords(input.title, path), 240);
  const canonical = `${SITE.url}${path === "/" ? "/" : path}`;
  return {
    title: clampText(title, 60),
    description,
    keywords,
    canonical,
    image: input.image || DEFAULT_OG_IMAGE,
    ogType: "website",
    twitterCard: "summary_large_image",
  };
}

export interface SeoCheck {
  label: string;
  ok: boolean;
  hint: string;
}

/** Lightweight on-page SEO score used by the admin SEO panel. */
export function scoreSeo(fields: { title: string; description: string; keywords: string; canonical: string }): {
  score: number;
  checks: SeoCheck[];
} {
  const titleLen = fields.title.trim().length;
  const descLen = fields.description.trim().length;
  const keywordCount = fields.keywords.split(",").map((k) => k.trim()).filter(Boolean).length;

  const checks: SeoCheck[] = [
    { label: "Title length (30–60 chars)", ok: titleLen >= 30 && titleLen <= 60, hint: `${titleLen} characters` },
    { label: "Description length (120–160 chars)", ok: descLen >= 120 && descLen <= 160, hint: `${descLen} characters` },
    { label: "At least 3 keywords", ok: keywordCount >= 3, hint: `${keywordCount} keywords` },
    { label: "Canonical URL set", ok: /^https?:\/\/.+/.test(fields.canonical.trim()), hint: fields.canonical || "Not set" },
    { label: "Focus phrase present in title", ok: /mysore|directory|business/i.test(fields.title), hint: "Brand + topic" },
  ];

  const passed = checks.filter((c) => c.ok).length;
  const score = Math.round((passed / checks.length) * 100);
  return { score, checks };
}